import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ApiRequestError } from "@mmm/contracts";
import App from "./App.js";
import { AppErrorBoundary } from "./components/shell/AppErrorBoundary.js";
import { ApiTransportError } from "./api/client.js";
import { pushToast } from "./components/shell/ToastProvider.js";
import "@mmm/ui/styles/reset.css";
import "@mmm/ui/styles/tokens.css";
import "@mmm/ui/styles/layout.css";
import "@mmm/ui/styles/components.css";
import "./styles/sidebar.css";
import "./styles/topbar.css";
import "./styles/map-canvas.css";
import "./styles/brand-screen.css";
import { t } from "@mmm/ui";

/** True once a caller (currently only api/commands.ts `run()`) has already
 * surfaced this error via its own toast — see the comment on `run()`. */
function alreadyReported(err: unknown): boolean {
  return Boolean(err && typeof err === "object" && (err as { reported?: boolean }).reported);
}

/** True only for the errors this app's API layer throws — deliberately
 * narrow so unrelated bugs (a rendering error, a third-party script) don't
 * spam a toast or loop. */
function isApiError(err: unknown): boolean {
  return err instanceof ApiRequestError || err instanceof ApiTransportError;
}

// Global backstop for mutation failures that slip past a local catch (e.g. a
// re-thrown ApiRequestError left unhandled by a call site). Reads are out of
// scope — api/useQuery.ts already has its own retry/backoff UX and never
// throws out to here.
window.addEventListener("unhandledrejection", (event) => {
  if (!isApiError(event.reason)) return;
  console.error("Unhandled API error:", event.reason);
  if (alreadyReported(event.reason)) return;
  pushToast({
    variant: "error",
    title: t("errors.somethingWentWrong"),
    // API-supplied messages are server-authored English by design (error
    // localization maps by code, not by rewording the server's text).
    message: event.reason instanceof Error ? event.reason.message : t("errors.unexpected"),
  });
});

window.onerror = (message, _source, _lineno, _colno, error) => {
  if (!isApiError(error)) return;
  console.error("Unhandled error:", message, error);
  if (alreadyReported(error)) return;
  pushToast({
    variant: "error",
    title: t("errors.somethingWentWrong"),
    message: error instanceof Error ? error.message : t("errors.unexpected"),
  });
};

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </StrictMode>
);
